import React from "react";
import {
  SELLER_PROFILE_FIELDS,
  OUTREACH_TYPES
} from "Pages/Dashboard/DeepResearch/components/SellerProfileFormBody";
// drFieldGrid / drInputGroup live here and this file styles itself with them.
// The settings route happens to get the sheet via another chunk today; importing
// it makes that a stated dependency rather than a load-order accident.
import "Pages/Dashboard/DeepResearch/deepResearch.scss";

// PDF-only rendering of the seller profile.
//
// The on-screen editor is a form, and printing a form gives you a page of empty
// boxes: browsers paint the control chrome but clip anything past the visible
// height of an input, so a three-row textarea loses everything below line three.
// This renders the same values as flowing text instead.
//
// Fields come from SELLER_PROFILE_FIELDS — the same list the form is built from
// — so a field added to the editor shows up here without a second edit.

// Everything on the profile that SELLER_PROFILE_FIELDS does not cover: the
// identity fields the page header owns, and the resource arrays edited by the
// form's "Resources & proof" section. Keys mirror buildEmptyProfile().
const EXTRA_FIELDS = [
  { key: "name", label: "Profile Name" },
  { key: "location", label: "Location" },
  { key: "website", label: "Website" },
  { key: "outreach_instructions", label: "Outreach Instructions" },
  { key: "segments", label: "Segments" },
  { key: "customers", label: "Customers" },
  { key: "use_cases", label: "Use Cases" },
  { key: "customer_stories", label: "Customer Stories" },
  { key: "testimonials", label: "Testimonials" }
];

// Values arrive as strings, arrays (chips) or occasionally objects, depending on
// the field and whether the profile was hydrated from the API or typed in.
const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        item && typeof item === "object"
          ? item.name || item.title || item.label || item.value || ""
          : String(item)
      )
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") return "";
  return String(value).trim();
};

// Deliberately reuses the editor's own classes (drInputGroup + label) so the
// export inherits the product styling rather than re-describing it. Only the
// control itself differs: a div instead of an input, because a real input prints
// as a box clipped to its visible height — a 3-row textarea would lose
// everything past line three.
const Row = ({ label, value, full }) => (
  <div
    className={`drInputGroup pdfAvoidBreak${full ? " drInputGroupFull" : ""}`}
  >
    <label>{label}</label>
    <div className="pdfFieldValue">{value}</div>
  </div>
);

const SellerProfilePrintDoc = ({ profile = {} }) => {
  // Rows carry the field key: labels are display text and nothing stops two
  // fields sharing one, and duplicate React keys make React silently drop a row.
  const rows = [...SELLER_PROFILE_FIELDS, ...EXTRA_FIELDS]
    .map(({ key, label }) => ({ key, label, value: toText(profile[key]) }))
    .filter(({ value }) => value);

  // Rendered from its own list so the PDF shows the readable label rather than
  // the stored "cold"/"nurtured"/"mixed" token.
  const outreachType = (
    OUTREACH_TYPES.find((t) => t.value === profile.outreach_type) || {}
  ).label || "";

  // half-width fields keep the editor's two-column rhythm; everything else runs
  // full width, same as on screen.
  const halfKeys = new Set(
    SELLER_PROFILE_FIELDS.filter((f) => f.half).map((f) => f.key)
  );

  return (
    <section className="pdfPrintOnly pdfDocSection">
      <h2 className="pdfDocSection__title">Seller Profile</h2>
      {rows.length === 0 && !outreachType ? (
        <div className="pdfFieldValue">
          No profile details have been filled in yet.
        </div>
      ) : (
        <div className="drFieldGrid">
          {outreachType && (
            <Row label="Outreach Type" value={outreachType} full />
          )}
          {rows.map(({ key, label, value }) => (
            <Row
              key={key}
              label={label}
              value={value}
              full={!halfKeys.has(key)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default SellerProfilePrintDoc;
